/**
 * UpSurv 2.0 — the HTTP side of sync.
 *
 * `SyncEngine` (sync.ts) owns the queue, the ordering, the backoff and the
 * conflict ladder. This file owns nothing but the wire: it turns one outbox row
 * into one request against the Pages Functions API in functions/api/[[route]].ts
 * and turns the response back into the result type the engine expects.
 *
 * Two decisions worth reading before changing anything here.
 *
 * ── 1. Field-initiated creation is its own call, on its own row ──────────────
 *
 * Spec §2.1: Chip and Tim are notified IMMEDIATELY when a surveyor creates a
 * survey on site — "no job may exist they don't know about". The notification
 * is fired by `POST /api/surveys`, which the device cannot call from a machine
 * room with no signal.
 *
 * `createSurvey` here is the wire half of an outbox row of its own (`kind:
 * 'create'`, store.ts), drained ahead of everything else and removed only by
 * its own acknowledgement. It was once a side effect of `pushDocument` —
 * cheaper, one row, one retry policy — and the cost of that was a creation
 * notice only as durable as the document row carrying it, which `ackSubmission`
 * deletes. A survey created and submitted in the same offline session lost the
 * notice entirely and nothing anywhere said so.
 *
 * `POST /api/surveys` is idempotent on the device-minted survey id, and the
 * notification is keyed `(survey, event)` server-side, so a replay after a lost
 * response cannot double-ping anyone. There is no client-side "already done"
 * cache: the row is the state, and it lives on disk.
 *
 * ── 2. Every failure is a TransportError ─────────────────────────────────────
 *
 * The engine retries everything and never drops a row, so the only thing a
 * status code decides here is the wording the surveyor sees in the sync pill.
 * `retryable: false` marks "the server refused this payload" as distinct from
 * "the link is down"; both keep the work.
 */

import { APP_BUILD } from '../core/build';
import { THUMB_CONTENT_TYPE } from '../core/photoThumb';
import { PHOTO_META_HEADER, encodePhotoMetaHeader } from './photo-file';
import { TRANSIENT_REFUSAL_CODES, TransportError } from './sync';
import type {
  DocumentPushRequest,
  DocumentPushResult,
  PhotoThumbUploadRequest,
  PhotoUploadRequest,
  PhotoUploadResult,
  RemoteAssignment,
  RemoteDocument,
  SealNoticeRequest,
  SubmissionRequest,
  SubmissionResult,
  SurveyCreationRequest,
  SurveyCreationResult,
  SurveyDiscardRequest,
  SurveyDiscardResult,
  SyncTransport,
} from './sync';
import type { PhotoRef, SurveyDocument, SurveyOrigin, SurveyStatus } from '../core/types';

/* ────────────────────────────── wire shapes ────────────────────────────── */

/** `GET /api/me`. */
export interface SurveyorIdentity {
  id: string;
  name: string;
}

/**
 * A row of `GET /api/surveys` — the lifecycle record, not the document.
 *
 * Mirrors `SurveyRecord` in src/db/queries.ts. `origin` and `assignedTo` are
 * carried because the assignment inbox has to build an openable document out of
 * this row alone when the server holds no document yet, and a survey that landed
 * on the phone as an assignment must not read as field-initiated afterwards.
 */
export interface RemoteSurveyRow {
  id: string;
  origin: SurveyOrigin;
  status: SurveyStatus;
  assignedTo?: string;
  createdBy?: string;
  buildingName?: string;
  address?: string;
  /** The address in four parts (D-039) — `surveys.city` / `state` / `zip`. */
  city?: string;
  state?: string;
  zip?: string;
  headRevision: number;
  createdAt: string;
  updatedAt: string;
}

interface RevisionBody {
  outcome: 'stored' | 'unchanged' | 'sealed';
  revision: number;
  contentHash?: string;
  conflict?: boolean;
}

interface SnapshotBody {
  revision: number;
  document: SurveyDocument | null;
  sealed: boolean;
}

export interface ApiClientOptions {
  /** Same-origin by default; the app and the API ship in one Pages project. */
  baseUrl?: string;
  /** Read fresh on every call so a re-linked device does not need a reload. */
  token: () => string | null;
  /** Stamped on pushes for sync forensics (see `x-device-id`). */
  deviceId?: string;
  /** Injected for tests; defaults to the global fetch. */
  fetchImpl?: typeof fetch;
}

/* ─────────────────────────────── the client ────────────────────────────── */

export class ApiClient implements SyncTransport {
  private readonly baseUrl: string;
  private readonly token: () => string | null;
  private readonly deviceId: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = (options.baseUrl ?? '/api').replace(/\/$/, '');
    this.token = options.token;
    this.deviceId = options.deviceId ?? 'device';
    this.fetchImpl = options.fetchImpl ?? ((...args) => globalThis.fetch(...args));
  }

  /* ───────────────────────────── plumbing ───────────────────────────── */

  private async request(
    path: string,
    init: RequestInit & { idempotencyKey?: string } = {},
  ): Promise<Response> {
    const token = this.token();
    if (!token) {
      throw new TransportError('This device is not linked to a surveyor yet.', {
        retryable: false,
        status: 401,
      });
    }

    const headers = new Headers(init.headers);
    headers.set('authorization', `Bearer ${token}`);
    headers.set('x-device-id', this.deviceId);
    // The build this request came from (round 8.1, task 1) — see src/core/build.ts.
    headers.set('x-upsurv-build', APP_BUILD);
    if (init.idempotencyKey) headers.set('idempotency-key', init.idempotencyKey);

    try {
      return await this.fetchImpl(`${this.baseUrl}${path}`, { ...init, headers });
    } catch (cause) {
      // A thrown fetch is the offline case: no response, nothing applied.
      throw new TransportError('No connection to the office.', { retryable: true, cause });
    }
  }

  private async json<T>(response: Response): Promise<T> {
    try {
      return (await response.json()) as T;
    } catch (cause) {
      throw new TransportError('The server sent something that was not JSON.', {
        retryable: true,
        status: response.status,
        cause,
      });
    }
  }

  /**
   * Turn a non-2xx response into the engine's error type, keeping the reason.
   *
   * ── WHY `details` IS READ HERE (the 2026-08-06 incident) ───────────────────
   *
   * This method used to be `detail = body.message ?? body.error ?? ''`. A schema
   * refusal answers `{"error":"invalid_body","details":[{"path":"questionId",
   * "message":"…"}]}` — no `message` at all — so the whole diagnosis collapsed
   * to the word "invalid_body" on the surveyor's screen and in the office. The
   * server knew exactly which field was wrong and the sentence threw it away.
   *
   * `details` is now read FIRST, because it is the only part of the body that
   * names a field. "Uploading a photo failed (400): questionId — expected a
   * question id" is something a person can act on; "invalid_body" is a shrug.
   */
  private async fail(response: Response, what: string): Promise<never> {
    let detail = '';
    let code = '';
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      code = typeof body.error === 'string' ? body.error : '';
      detail = describeDetails(body.details) || body.message || body.error || '';
    } catch {
      /* a body that will not parse adds nothing to the message */
    }
    /*
     * ── RETRYABILITY IS READ, NOT INFERRED (2026-08-10) ──────────────────────
     *
     * This used to be the whole decision:
     *
     *     status >= 500 || status === 429 || status === 408
     *
     * — a numeric range standing in for a judgement it cannot make. `400` is
     * both "this body is malformed" and "this body did not finish arriving";
     * `409` is both "this survey is sealed" and "its document has not synced
     * yet". One of each pair is permanent and the other clears itself in
     * seconds, and the range calls both of them permanent.
     *
     * The server already names the condition in `error`. So the code is asked
     * first and the range is only the fallback for a response that carries no
     * code at all. `TRANSIENT_REFUSAL_CODES` (db/sync.ts) is the one list, read
     * by this method and by the engine's parking decision, so the wire and the
     * queue cannot come to different conclusions about the same response.
     */
    const retryable =
      TRANSIENT_REFUSAL_CODES.has(code) ||
      response.status >= 500 ||
      response.status === 429 ||
      response.status === 408;
    throw new TransportError(
      `${what} failed (${response.status})${detail ? `: ${detail}` : ''}`,
      { retryable, status: response.status, ...(code ? { code } : {}) },
    );
  }

  /* ───────────────────────────── identity ───────────────────────────── */

  async me(): Promise<SurveyorIdentity> {
    const response = await this.request('/me');
    if (!response.ok) await this.fail(response, 'Sign-in check');
    return this.json<SurveyorIdentity>(response);
  }

  async listSurveys(): Promise<RemoteSurveyRow[]> {
    const response = await this.request('/surveys');
    if (!response.ok) await this.fail(response, 'Loading your surveys');
    const body = await this.json<{ surveys: RemoteSurveyRow[] }>(response);
    return body.surveys;
  }

  /* ──────────────────────────── SyncTransport ───────────────────────── */

  /**
   * The assignment inbox's read (spec §2.1).
   *
   * SCOPING IS THE SERVER'S, and that is the point: `GET /api/surveys` is
   * `listSurveysFor(db, <the surveyor this token resolves to>)`, so the list a
   * device receives is exactly the list it is entitled to. There is no surveyor
   * id in this request to get wrong, and nothing on this side filters — a client
   * -side filter would imply the server had sent something it should not have.
   */
  async listAssignments(): Promise<RemoteAssignment[]> {
    const rows = await this.listSurveys();
    return rows.map((row) => ({
      surveyId: row.id,
      // Defaulted rather than trusted-absent: a row with no origin would
      // otherwise produce a document that fails SurveyDocumentSchema on push.
      origin: row.origin ?? 'assigned',
      status: row.status,
      ...(row.assignedTo ? { assignedTo: row.assignedTo } : {}),
      ...(row.createdBy ? { createdBy: row.createdBy } : {}),
      ...(row.buildingName ? { buildingName: row.buildingName } : {}),
      ...(row.address ? { address: row.address } : {}),
      ...(row.city ? { city: row.city } : {}),
      ...(row.state ? { state: row.state } : {}),
      ...(row.zip ? { zip: row.zip } : {}),
      headRevision: row.headRevision ?? 0,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
    }));
  }

  /**
   * Create the survey server-side. This is what fires spec §2.1's "Chip and Tim
   * are notified immediately" — immediately meaning the first moment there is a
   * network, which is the only meaning available in a basement.
   *
   * Building name and address are read off answers B1 / B2, which is where they
   * live; the office gets a notice naming the building rather than a bare id
   * whenever the surveyor typed one before losing signal. The city, state and
   * ZIP ride along from B2a / B2b / B2c the same way (D-039).
   */
  async createSurvey(request: SurveyCreationRequest): Promise<SurveyCreationResult> {
    const { document } = request;
    const buildingName = readText(document, 'B1');
    const address = readText(document, 'B2');
    const city = readText(document, 'B2a');
    const state = readText(document, 'B2b');
    const zip = readText(document, 'B2c');
    const response = await this.request('/surveys', {
      method: 'POST',
      idempotencyKey: request.idempotencyKey,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        surveyId: request.surveyId,
        createdAt: document.createdAt,
        // Never the document's own status: a survey submitted offline before it
        // was ever announced is still being CREATED here, and `POST /api/surveys`
        // refuses a submitted status outright. The submission that follows sets
        // the real lifecycle state.
        status: 'in_progress',
        ...(buildingName ? { buildingName } : {}),
        ...(address ? { address } : {}),
        ...(city ? { city } : {}),
        ...(state ? { state } : {}),
        ...(zip ? { zip } : {}),
      }),
    });

    // 201 is the create; 200 is the idempotent replay. Anything else, including
    // a 403 for someone else's id, is a real failure the queue must retry or
    // surface — never a silent skip that would lose the notification.
    if (!response.ok) await this.fail(response, 'Telling the office about this survey');
    return { outcome: response.status === 201 ? 'created' : 'already_created' };
  }

  async pushDocument(request: DocumentPushRequest): Promise<DocumentPushResult> {
    const response = await this.request(`/surveys/${encodeURIComponent(request.surveyId)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      idempotencyKey: request.idempotencyKey,
      body: JSON.stringify(request.document),
    });

    // 410 Gone: the survey was withdrawn (D-012). An outcome, not an error —
    // the engine drops the row instead of retrying a request that can only ever
    // be refused, and keeps the local document untouched.
    if (response.status === 410) return { outcome: 'discarded', serverRevision: 0 };

    // 409 carries a real body when the survey is sealed — that is an outcome,
    // not an error, and the engine has a branch for it.
    if (!response.ok && response.status !== 409) await this.fail(response, 'Uploading the survey');

    const body = await this.json<RevisionBody>(response);
    if (body.outcome === 'sealed') {
      return { outcome: 'sealed', serverRevision: body.revision, conflict: body.conflict ?? true };
    }
    return { outcome: body.outcome, serverRevision: body.revision };
  }

  async submitSurvey(request: SubmissionRequest): Promise<SubmissionResult> {
    const response = await this.request(
      `/surveys/${encodeURIComponent(request.surveyId)}/submit`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        idempotencyKey: request.idempotencyKey,
        body: JSON.stringify({ document: request.document }),
      },
    );

    if (!response.ok && response.status !== 409) await this.fail(response, 'Submitting the survey');

    const body = await this.json<RevisionBody>(response);
    return {
      // 'sealed' here means the server already holds a submission for this
      // survey: the replay case, which settles exactly like a first submit.
      outcome: body.outcome === 'stored' ? 'submitted' : 'already_submitted',
      serverRevision: body.revision,
    };
  }

  /**
   * Withdraw a survey the office already knows about (D-012).
   *
   * Carries nothing but the id: the device forgot the document before this row
   * was queued, and the server does not need it to mark a record discarded.
   *
   * Both "not found" answers settle as `already_gone` rather than throwing. The
   * device's promise was "the office will not be left holding this job", and a
   * server with no such survey has already kept it — retrying forever against a
   * 404 would only park the sync pill in its alarm state over a job that does
   * not exist.
   */
  async discardSurvey(request: SurveyDiscardRequest): Promise<SurveyDiscardResult> {
    const response = await this.request(
      `/surveys/${encodeURIComponent(request.surveyId)}/discard`,
      { method: 'POST', idempotencyKey: request.idempotencyKey },
    );

    if (response.status === 404) return { outcome: 'already_gone' };
    if (!response.ok) await this.fail(response, 'Discarding the survey');

    const body = await this.json<{ outcome?: string }>(response);
    return {
      outcome: body.outcome === 'already_discarded' ? 'already_discarded' : 'discarded',
    };
  }

  /**
   * `POST /api/surveys/:id/sealed` — "sealed on this phone, N photos to come"
   * (D-052 §3). A 404 (the office never heard of it) and a 410 (withdrawn)
   * both mean there is nobody to tell, and resolve; anything else that is not
   * OK is a transport error and the row retries under backoff.
   */
  async notifySealed(request: SealNoticeRequest): Promise<void> {
    const response = await this.request(
      `/surveys/${encodeURIComponent(request.surveyId)}/sealed`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        idempotencyKey: request.idempotencyKey,
        body: JSON.stringify({ sealedAt: request.sealedAt, photosOwed: request.photosOwed }),
      },
    );
    if (response.status === 404 || response.status === 410) return;
    if (!response.ok) await this.fail(response, 'Telling the office the survey is sealed');
  }

  async uploadPhoto(request: PhotoUploadRequest): Promise<PhotoUploadResult> {
    /*
     * THE BODY IS THE PICTURE — raw bytes, nothing else (2026-08-19).
     *
     * This used to build a multipart form: a JSON `meta` part and a `photo`
     * part made from the stored Blob. On an iPhone running the installed app,
     * WebKit serialised that part as NOTHING — the office saw a multipart header
     * and `content-length: 0`, on every photo, for twelve days (see the module
     * header of src/db/photo-file.ts for the whole incident). A `Uint8Array`
     * body is bytes the page already holds; there is no Blob handle for any
     * browser to fail to read and no multipart boundary for any parser to miss.
     *
     * The metadata travels in one header, base64-encoded; the content type is
     * the request's own — the type the capture screen resolved and the PhotoRef
     * has carried all along — so the office never has to guess it from a part.
     * `bytes` is the capture's own count, which is how the office tells a body
     * that did not all arrive from one that did: received ≠ declared is
     * `incomplete_upload`, and the device tries again.
     */
    const meta = {
      id: request.photoId,
      surveyId: request.surveyId,
      sectionInstanceId: request.sectionInstanceId,
      ...(request.questionId ? { questionId: request.questionId } : {}),
      ...(request.shotListId ? { shotListId: request.shotListId } : {}),
      // 2.4.0, D-038: accepted by PhotoMetaSchema but never stored — the label
      // travels in the document JSON, not the upload route.
      ...(request.label ? { label: request.label } : {}),
      // The capture instant from the PhotoRef, never `Date.now()`: a photo
      // taken on Monday and drained on Wednesday still says Monday.
      capturedAt: request.capturedAt,
      contentType: request.contentType,
      bytes: request.data.byteLength,
    };

    const response = await this.request('/photos', {
      method: 'POST',
      idempotencyKey: request.idempotencyKey,
      headers: {
        'content-type': request.contentType,
        [PHOTO_META_HEADER]: encodePhotoMetaHeader(meta),
      },
      // A copy into a plain ArrayBuffer-backed view, so the body is exactly
      // these bytes whatever the store handed back (a SharedArrayBuffer view or
      // a subarray would not be a valid BodyInit everywhere).
      body: new Uint8Array(request.data),
    });

    if (!response.ok) await this.fail(response, 'Uploading a photo');
    const body = await this.json<{ photo: PhotoRef }>(response);
    return { outcome: 'uploaded', r2Key: body.photo.r2Key };
  }

  /**
   * The thumbnail beside the picture — round 8.1, task 3. The body is the
   * JPEG, raw, for the reason `uploadPhoto` gives; no metadata header,
   * because the office already holds the photo this is a small copy of and
   * derives the key from its record. No idempotency key either: the same
   * bytes on the same key are the same object, however many times they land.
   */
  async uploadPhotoThumb(request: PhotoThumbUploadRequest): Promise<void> {
    const response = await this.request(
      `/photos/${encodeURIComponent(request.surveyId)}/${encodeURIComponent(request.photoId)}/thumb`,
      {
        method: 'PUT',
        headers: { 'content-type': THUMB_CONTENT_TYPE },
        body: new Uint8Array(request.data),
      },
    );
    if (!response.ok) await this.fail(response, 'Uploading a photo preview');
  }

  /**
   * Read a photo the office holds back to this device — round 8, task 4.
   *
   * The phone asks only for a picture it has lost the bytes of (see
   * `resolveOriginalBlob` in src/ui/state/photoBytes.ts). Null is "the office
   * has no such picture" — a 404, which the resolver renders as the same
   * empty tile a lost picture always had; anything else the server refuses,
   * and a dropped connection, throws through `fail()` and `request()` so the
   * caller can tell a miss from a failure to ask.
   *
   * `size: 'thumb'` (round 8.1, task 3) asks for the small copy instead —
   * `?size=thumb` — and a 404 there means the office holds the photo but no
   * thumb for it, which the caller treats the same way: no small picture.
   */
  async fetchPhoto(surveyId: string, photoId: string, size?: 'thumb'): Promise<Blob | null> {
    const response = await this.request(
      `/photos/${encodeURIComponent(surveyId)}/${encodeURIComponent(photoId)}${
        size === 'thumb' ? '?size=thumb' : ''
      }`,
    );
    if (response.status === 404) return null;
    if (!response.ok) await this.fail(response, 'Fetching a photo');
    return response.blob();
  }

  async fetchDocument(surveyId: string): Promise<RemoteDocument | null> {
    const response = await this.request(`/surveys/${encodeURIComponent(surveyId)}`);
    if (response.status === 404) return null;
    if (!response.ok) await this.fail(response, 'Fetching the survey');

    const body = await this.json<SnapshotBody>(response);
    if (!body.document) return null;
    return {
      document: body.document,
      serverRevision: body.revision,
      sealed: body.sealed,
    };
  }
}

/* ───────────────────────────────── helpers ─────────────────────────────── */

/**
 * `[{path:'questionId', message:'expected …'}]` → `"questionId — expected …"`.
 *
 * Defensive about shape on purpose: this parses a body from the network, and a
 * malformed `details` must degrade to "" so `fail()` falls through to `message`
 * rather than throwing a second error on top of the first one. Capped at three
 * fields — the surveyor needs to know WHICH field, not a dump.
 */
function describeDetails(details: unknown): string {
  if (!Array.isArray(details)) return '';
  const parts: string[] = [];
  for (const entry of details) {
    if (entry === null || typeof entry !== 'object') continue;
    const { path, message } = entry as { path?: unknown; message?: unknown };
    const field = typeof path === 'string' ? path : '';
    const text = typeof message === 'string' ? message : '';
    if (!field && !text) continue;
    parts.push(field && text ? `${field} — ${text}` : field || text);
    if (parts.length === 3) break;
  }
  return parts.join('; ');
}

/** B1 / B2 off the answer map. Building answers carry no scope prefix. */
function readText(document: SurveyDocument, questionId: string): string {
  const value = document.answers[questionId];
  return value?.kind === 'text' ? value.value.trim() : '';
}
